import React, { useEffect, useState } from "react";
import { Target, Pencil, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { getHoldingsSummary } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmt$, fmt$2 } from "@/lib/format";
import { solveIncomeTarget } from "@/lib/incomePlanner";

// Personal monthly-income goal vs. the forward income of the user's real (synced) holdings.
export default function IncomeGoalProgress({ className = "", testId = "income-goal" }) {
  const [goal, setGoal] = useState(null);
  const [suggested, setSuggested] = useState(null);
  const [totals, setTotals] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/income-goal"), getHoldingsSummary()]).then(([g, s]) => {
      setGoal(g.data.goal);
      setSuggested(g.data.suggested);
      const d = s.data || {};
      setTotals({
        monthly: Number(d.forward_monthly_income ?? d.totals?.forward_monthly_income ?? 0),
        annual: Number(d.forward_annual_income ?? d.totals?.forward_annual_income ?? 0),
        value: Number(d.totals?.total_value_ex_cash || d.total_value || d.totals?.total_value || 0),
        yieldPct: d.totals?.current_yield_pct,
        positions: Number(d.position_count ?? d.totals?.position_count ?? 0),
      });
    }).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const save = async (vals) => {
    const r = await api.put("/income-goal", vals);
    setGoal(r.data.goal);
    setEditing(false);
    toast.success("Income goal saved");
  };

  if (!loaded || !totals || totals.positions === 0) return null;

  if (!goal || editing) {
    return (
      <GoalForm
        className={className}
        testId={testId}
        initial={goal || suggested}
        suggestedFrom={!goal && suggested?.portfolio_name}
        onSave={save}
        onCancel={goal ? () => setEditing(false) : null}
      />
    );
  }
  return <GoalBar className={className} testId={testId} goal={goal} totals={totals} onEdit={() => setEditing(true)} />;
}

function GoalBar({ goal, totals, onEdit, className, testId }) {
  const target = Number(goal.monthly_target);
  const pct = target > 0 ? (totals.monthly / target) * 100 : 0;
  const gap = Math.max(0, target - totals.monthly);
  const y = totals.yieldPct != null ? Number(totals.yieldPct) : (totals.value > 0 ? (totals.annual / totals.value) * 100 : 0);
  const plan = gap > 0 && y > 0 ? solveIncomeTarget({ monthlyTarget: gap, years: goal.years, growthPct: goal.growth_pct, currentYieldPct: y, drip: true }) : null;
  return (
    <div className={`rounded-lg border border-secondary/30 bg-secondary/[0.04] p-4 ${className}`} data-testid={testId}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="mono text-[10px] uppercase tracking-widest text-secondary flex items-center gap-2">
          <Target className="w-3.5 h-3.5" /> Income goal
        </div>
        <button onClick={onEdit} className="text-xs text-primary font-semibold inline-flex items-center gap-1 hover:underline" data-testid={`${testId}-edit`}>
          <Pencil className="w-3 h-3" /> Edit goal
        </button>
      </div>
      <div className="mt-2 flex items-baseline gap-2 flex-wrap">
        <span className="serif text-2xl tabular-nums" data-testid={`${testId}-current`}>{fmt$2(totals.monthly)}</span>
        <span className="text-sm text-muted-foreground">of <b className="text-foreground" data-testid={`${testId}-target`}>{fmt$(target)}</b>/mo</span>
        <span className="mono text-sm text-secondary ml-auto" data-testid={`${testId}-pct`}>{Math.min(999, pct).toFixed(1)}%</span>
      </div>
      <div className="mt-2 h-2.5 rounded-full bg-muted overflow-hidden" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-full rounded-full bg-gradient-to-r from-secondary to-amber-400 transition-[width] duration-700" style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <div className="mt-2 text-xs text-muted-foreground" data-testid={`${testId}-gap`}>
        {gap <= 0 ? (
          <span className="text-emerald-700 font-semibold">Goal reached — your holdings already pay {fmt$2(totals.monthly)}/mo in forward income.</span>
        ) : plan ? (
          <>{fmt$2(gap)}/mo to go · invest about <b className="text-foreground">{fmt$(plan.lumpNow)}</b> at today&apos;s {y.toFixed(2)}% yield,
            or <b className="text-foreground">{fmt$(plan.dca.monthly)}/mo</b> for {goal.years} years ({goal.growth_pct}% dividend growth, DRIP).</>
        ) : <>{fmt$2(gap)}/mo to go.</>}
      </div>
    </div>
  );
}

function GoalForm({ initial, suggestedFrom, onSave, onCancel, className, testId }) {
  const [target, setTarget] = useState(initial?.monthly_target ?? "");
  const [years, setYears] = useState(initial?.years ?? 10);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!(Number(target) > 0)) { toast.error("Enter a monthly income goal above $0"); return; }
    setBusy(true);
    try {
      await onSave({ monthly_target: Number(target), years: Number(years) || 10, growth_pct: Number(initial?.growth_pct ?? 3) });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Could not save goal");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className={`rounded-lg border border-dashed border-secondary/40 bg-secondary/[0.03] p-4 ${className}`} data-testid={`${testId}-form`}>
      <div className="mono text-[10px] uppercase tracking-widest text-secondary flex items-center gap-2">
        <Target className="w-3.5 h-3.5" /> {onCancel ? "Edit income goal" : "Set an income goal"}
      </div>
      {suggestedFrom && <p className="text-xs text-muted-foreground mt-1">Pre-filled from your Income Planner on “{suggestedFrom}”.</p>}
      <div className="mt-3 flex items-end gap-2 flex-wrap">
        <label className="text-xs text-muted-foreground">Monthly goal
          <div className="relative mt-1">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 mono text-sm text-muted-foreground">$</span>
            <Input type="number" min={1} step="any" value={target} onChange={(e) => setTarget(e.target.value)} className="pl-6 w-32 mono" data-testid={`${testId}-input`} />
          </div>
        </label>
        <label className="text-xs text-muted-foreground">Years
          <Input type="number" min={1} max={40} value={years} onChange={(e) => setYears(e.target.value)} className="mt-1 w-20 mono" data-testid={`${testId}-years`} />
        </label>
        <Button type="submit" size="sm" className="rounded-full" disabled={busy} data-testid={`${testId}-save`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}Track goal
        </Button>
        {onCancel && (
          <Button type="button" size="sm" variant="ghost" className="rounded-full" onClick={onCancel} data-testid={`${testId}-cancel`}>
            <X className="w-3.5 h-3.5 mr-1" />Cancel
          </Button>
        )}
      </div>
    </form>
  );
}
